import { Head, Link, router, useForm } from '@inertiajs/react'
import { useState } from 'react'
import Layout from '../../components/Layout'
import Modal from '../../components/shared/Modal'
import { LABELS } from './labels'
import type { ChartSummary } from './types'

interface Props {
  active_tab: string
  tab: 'active' | 'archived'
  counts: { active: number; archived: number }
  charts: ChartSummary[]
  copy_sources: { id: number; name: string }[]
  can_create: boolean
}

export default function AccountabilityChartsIndex({ tab, counts, charts, copy_sources, can_create }: Props) {
  const [showNew, setShowNew] = useState(false)

  return (
    <Layout>
      <Head title={LABELS.features} />
      <div className="max-w-screen-2xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-h2 font-semibold text-text-primary">{LABELS.features}</h1>
            <p className="text-text-secondary mt-1">Define the {LABELS.seats.toLowerCase()} across your organization and who owns each one.</p>
          </div>
          {can_create && (
            <button
              onClick={() => setShowNew(true)}
              className="bg-pinnacle-red text-white px-5 py-2.5 rounded-button font-semibold hover:bg-pinnacle-red-dark transition-colors"
            >
              New Chart
            </button>
          )}
        </div>

        <div className="border-b border-stone mb-6">
          {(['active', 'archived'] as const).map(t => (
            <Link
              key={t}
              href={`/accountability_charts?tab=${t}`}
              className={`px-4 py-3 inline-block text-base font-medium border-b-2 ${tab === t ? 'border-pinnacle-red text-pinnacle-red' : 'border-transparent text-text-secondary hover:text-text-primary'}`}
            >
              {t === 'active' ? 'Active' : 'Archived'}
              <span className="ml-2 text-sm bg-stone text-text-primary px-2 py-0.5 rounded-badge">{counts[t]}</span>
            </Link>
          ))}
        </div>

        {charts.length === 0 ? (
          <div className="bg-white rounded-card shadow-card p-12 text-center">
            <p className="text-text-secondary text-lg">No {tab === 'active' ? 'active' : 'archived'} charts yet.</p>
            {tab === 'active' && can_create && (
              <button
                onClick={() => setShowNew(true)}
                className="mt-4 bg-pinnacle-red text-white px-5 py-2 rounded-button font-semibold hover:bg-pinnacle-red-dark"
              >
                Create your first chart
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {charts.map(chart => (
              <Link
                key={chart.id}
                href={`/accountability_charts/${chart.id}`}
                className="bg-white rounded-card shadow-card hover:shadow-card-hover transition-shadow p-5 block"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-h4 font-semibold text-text-primary truncate">{chart.name}</h3>
                  <div className="shrink-0 flex items-center gap-2">
                    {chart.is_published && (
                      <span className="px-2 py-0.5 rounded-badge text-xs font-semibold bg-sea/15 text-sea">Published</span>
                    )}
                    {/* Publishing is a chart-level action: a viewer who can see
                        a published chart here gets no button at all. */}
                    {tab === 'active' && chart.permissions?.can_publish && (
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          const action = chart.is_published ? 'unpublish' : 'publish'
                          router.post(`/accountability_charts/${chart.id}/${action}`, {}, { preserveScroll: true })
                        }}
                        className={`px-2.5 py-1 rounded-button text-xs font-semibold border transition-colors ${
                          chart.is_published
                            ? 'border-stone text-text-secondary hover:bg-stone/30'
                            : 'border-pinnacle-red text-pinnacle-red hover:bg-pinnacle-red/5'
                        }`}
                      >
                        {chart.is_published ? 'Unpublish' : 'Publish'}
                      </button>
                    )}
                  </div>
                </div>
                {chart.description && <p className="text-text-secondary text-sm mt-1 line-clamp-2">{chart.description}</p>}
                <div className="flex items-center gap-4 mt-4 text-sm text-text-secondary">
                  <span>{chart.seat_count} {chart.seat_count === 1 ? LABELS.seat.toLowerCase() : LABELS.seats.toLowerCase()}</span>
                  {chart.last_updated_at && (
                    <span>Updated {new Date(chart.last_updated_at).toLocaleDateString()}</span>
                  )}
                </div>
                {chart.created_by && (
                  <div className="mt-3 text-xs text-text-muted">Created by {chart.created_by.name}</div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>

      {showNew && (
        <NewChartModal copySources={copy_sources} onClose={() => setShowNew(false)} />
      )}
    </Layout>
  )
}

function NewChartModal({ copySources, onClose }: { copySources: { id: number; name: string }[]; onClose: () => void }) {
  const [mode, setMode] = useState<'blank' | 'copy'>('blank')
  const { data, setData, post, processing, errors } = useForm({
    accountability_chart: { name: '', description: '' },
    source_chart_id: '' as string | number,
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/accountability_charts', {
      onSuccess: onClose,
      preserveScroll: true,
    })
  }

  return (
    <Modal isOpen onClose={onClose} title="New Chart">
      <form onSubmit={submit} className="space-y-4">
        <div className="flex gap-2">
          <button type="button" onClick={() => setMode('blank')}
            className={`flex-1 px-3 py-2 rounded-button border ${mode === 'blank' ? 'border-pinnacle-red text-pinnacle-red bg-pinnacle-red/5' : 'border-stone text-text-secondary'}`}>
            Blank chart
          </button>
          <button type="button" onClick={() => setMode('copy')} disabled={copySources.length === 0}
            className={`flex-1 px-3 py-2 rounded-button border disabled:opacity-50 ${mode === 'copy' ? 'border-pinnacle-red text-pinnacle-red bg-pinnacle-red/5' : 'border-stone text-text-secondary'}`}>
            Copy from existing
          </button>
        </div>

        <div>
          <label className="block text-sm font-semibold text-text-primary mb-1">Name</label>
          <input
            type="text"
            value={data.accountability_chart.name}
            onChange={e => setData('accountability_chart', { ...data.accountability_chart, name: e.target.value })}
            className="w-full px-3 py-2 border border-charcoal/20 rounded-button focus:outline-none focus:border-charcoal"
            placeholder="Results Ownership Chart"
            autoFocus
            required
          />
          {errors['accountability_chart.name'] && <p className="text-pinnacle-red text-sm mt-1">{errors['accountability_chart.name']}</p>}
        </div>

        {mode === 'copy' && (
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-1">Copy from</label>
            <select
              value={data.source_chart_id}
              onChange={e => setData('source_chart_id', e.target.value)}
              className="w-full px-3 py-2 border border-charcoal/20 rounded-button focus:outline-none focus:border-charcoal"
              required
            >
              <option value="">Select a chart…</option>
              {copySources.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-text-primary font-semibold hover:bg-stone/40 rounded-button">Cancel</button>
          <button type="submit" disabled={processing} className="bg-pinnacle-red text-white px-5 py-2 rounded-button font-semibold hover:bg-pinnacle-red-dark disabled:opacity-50">
            {processing ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
